import React from 'react';
import { 
  Chip, 
  Tooltip, 
  Typography, 
  Box, 
  LinearProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { 
  Memory as CacheIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import useConversationCache from '../../hooks/useConversationCache';

/**
 * Component to display conversation cache status and efficiency metrics
 */
const CacheStatusIndicator = ({ 
  conversationId, 
  variant = 'chip', // 'chip', 'detailed', 'minimal'
  showRecommendations = false 
}) => {
  const {
    cacheStatus,
    cacheEfficiency,
    loading,
    formatCacheStatus,
    getCacheRecommendations,
    isCached,
    isExpiringSoon,
    tokensSaved
  } = useConversationCache(conversationId);

  if (!conversationId || loading) {
    return variant === 'chip' ? (
      <Chip 
        size="small" 
        icon={<CacheIcon />} 
        label="Loading..." 
        color="default"
      />
    ) : null;
  }

  const formatted = formatCacheStatus();
  const recommendations = getCacheRecommendations();

  // Chip variant - compact display
  if (variant === 'chip') {
    const chipColor = isCached ? (isExpiringSoon ? 'warning' : 'success') : 'default';
    const chipLabel = isCached ? 
      `Cached (${formatted?.timeRemaining || '0m'})` : 
      'Not Cached';

    return (
      <Tooltip title={
        <Box>
          <Typography variant="body2">
            {isCached ? 'Conversation is cached' : 'No cache available'}
          </Typography>
          {tokensSaved > 0 && (
            <Typography variant="caption">
              Tokens saved: {formatted?.tokensSaved || '0'}
            </Typography>
          )}
        </Box>
      }>
        <Chip 
          size="small"
          icon={<CacheIcon />}
          label={chipLabel}
          color={chipColor}
          variant="outlined"
        />
      </Tooltip>
    );
  }

  // Minimal variant - just an icon with tooltip
  if (variant === 'minimal') {
    const iconColor = isCached ? (isExpiringSoon ? 'warning' : 'success') : 'disabled';
    
    return (
      <Tooltip title={
        <Box>
          <Typography variant="body2">
            Cache Status: {isCached ? 'Active' : 'Inactive'}
          </Typography>
          {formatted && (
            <>
              <Typography variant="caption">
                Time remaining: {formatted.timeRemaining}
              </Typography>
              <br />
              <Typography variant="caption">
                Tokens saved: {formatted.tokensSaved}
              </Typography>
            </>
          )}
        </Box>
      }>
        <CacheIcon color={iconColor} fontSize="small" />
      </Tooltip>
    );
  }

  // Detailed variant - full card with metrics
  if (variant === 'detailed') {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CacheIcon color={isCached ? 'primary' : 'disabled'} />
            <Typography variant="h6">
              Cache Status
            </Typography>
            <Chip 
              size="small"
              label={isCached ? 'Active' : 'Inactive'}
              color={isCached ? (isExpiringSoon ? 'warning' : 'success') : 'default'}
            />
          </Box>

          {formatted && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Messages Cached:
                </Typography>
                <Typography variant="body2">
                  {formatted.messagesCount}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Tokens Saved:
                </Typography>
                <Typography variant="body2">
                  {formatted.tokensSaved}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Cache Efficiency:
                </Typography>
                <Typography variant="body2">
                  {formatted.efficiency}
                </Typography>
              </Box>

              {isCached && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Time Remaining:
                  </Typography>
                  <Typography variant="body2" color={isExpiringSoon ? 'warning.main' : 'text.primary'}>
                    {formatted.timeRemaining}
                  </Typography>
                </Box>
              )}

              {/* Cache efficiency progress bar */}
              {cacheEfficiency?.cacheHitRatio > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    Cache Hit Ratio
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={cacheEfficiency.cacheHitRatio * 100}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Recommendations */}
          {showRecommendations && recommendations.length > 0 && (
            <Box mt={2}>
              {recommendations.map((rec, index) => (
                <Alert 
                  key={index}
                  severity={rec.type}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  {rec.message}
                </Alert>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default CacheStatusIndicator;